import { Environment } from 'src/utils/Environment';
import { LocalStorage } from 'src/utils/LocalStorage';

import { readCookie } from 'src/utils/Cookies';
import { AGENT_ID_COOKIE_NAME, sendGACookiesToAnalytics } from './analytics/analytics';
import { setIntervalWithTimeLimit } from './utils';

export const HIDE_INTERCOM_KEY = 'hideIntercom';
export const MAX_POLLING_TIME = 5000;
export const POLLING_INTERVAL = 50;

export class IntercomWrapper {
  static get isEnabled() {
    return !!(window.Intercom && !Boolean(LocalStorage.tryGetJSONItem(HIDE_INTERCOM_KEY)));
  }

  static clearIntercomInterval?: () => void;

  static shouldBeEnabled: boolean;

  static boot(options = {}) {

    if (!window.Intercom) return false;

    window.Intercom('boot', {
      app_id: Environment.current.isDev ? 'kv73vio0' : 'z49p7d1f',
      ...options,
    });

    // The only way to update Intercom with merchant status during a conversation given the limitations of the IntercomAPI
    // https://developers.intercom.com/installing-intercom/docs/intercom-javascript
    window.Intercom('onUnreadCountChange', () => {
      const isCloverMerchant = Environment.current.isExistingMerchant;

      if (isCloverMerchant) {
        IntercomWrapper.update({ isCloverMerchant });
      } else {
        IntercomWrapper.update({ agentId: readCookie(AGENT_ID_COOKIE_NAME) });
      }
    });

    addGaconnectorToIntercom();

    return true;
  }

  static shutdown() {
    if (!window.Intercom) {
      return false;
    }

    window.Intercom('shutdown');

    return true;
  }

  static update(options = {}) {
    if (!IntercomWrapper.isEnabled) return false;

    window.Intercom('update', options);

    return true;
  }

  static trackEvent(eventName: string, options = {}) {
    if (!IntercomWrapper.isEnabled) return;

    window.Intercom('trackEvent', eventName, options);
  }

  static hideLauncher() {
    if (!IntercomWrapper.isEnabled) return false;

    window.Intercom('update', {
      hide_default_launcher: true,
    });

    return true;
  }

  static showLauncher() {
    if (!IntercomWrapper.isEnabled) return false;

    window.Intercom('update', {
      hide_default_launcher: false,
    });

    return true;
  }

  static setShowIntercom(shouldShow: boolean) {
    this.shouldBeEnabled = shouldShow;
    LocalStorage.trySetItem(HIDE_INTERCOM_KEY, (!shouldShow).toString());
    if (this.clearIntercomInterval) {
      this.clearIntercomInterval();
    }

    setIntervalWithTimeLimit(clearFn => {
      this.clearIntercomInterval = clearFn;
      if (this.shouldBeEnabled ? this.showLauncher() : this.hideLauncher()) {
        clearFn();
      }
    }, MAX_POLLING_TIME, POLLING_INTERVAL);
  }

  static disableAndHide() {
    this.setShowIntercom(false);
  }

  static enableAndShow() {
    this.setShowIntercom(true);
  }
}

function addGaconnectorToIntercom() {
  if (!window.gaconnector) return;

  window.gaconnector.setCallback(sendGACookiesToAnalytics);
}
